import React, { useEffect, useState } from 'react';

import { Skeleton } from '@material-ui/lab';
import { usePerformanceGridData } from 'components/Views/Hooks/dataHooks';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledBox, StyledGrid, StyledTooltip, StyledTypography } from 'styles/common';

import { camelCase, getColorFromKey } from 'constants/helper';
import theme from 'constants/theme';

import { onClickPreCheck } from './CohortCreationFeedback';
import { useCohortCreationContext } from './CohortSidePanelContext';

const CohortCreation9BoxGrid = ({ sectionId, dataKey, isAllEmployeesSelected, accessibleRole, moduleName }) => {
  const [loading, setLoading] = useState(false);

  const [gridDefinition, setGridDefinition] = useState(null);

  const {
    state: { selectedOptions },
    appendNestedSelectedValue,
    deleteNestedSelectedValue,
  } = useCohortCreationContext();

  const { data: gridDefinitionV1, isLoading: loadingV1 } = usePerformanceGridData({
    // cohort id will always be -1 as its not dependent on cohort for this use case
    cohort: { id: -1 },
    source: selectedOptions?.review?.source,
    enabled: !!selectedOptions?.review?.source,
  });

  useEffect(() => {
    setGridDefinition(gridDefinitionV1?.reverse());
  }, [gridDefinitionV1]);

  if (loading || loadingV1) {
    return <Skeleton width="70%" height="2rem" />;
  }

  if (!gridDefinition) {
    return null;
  }

  const onSelectHandler = ({ row, column, id, isSelected }) => {
    if (isSelected) {
      deleteNestedSelectedValue(sectionId, dataKey, { row, column, id });
    } else {
      appendNestedSelectedValue(sectionId, dataKey, { row, column, id });
    }
  };

  return (
    <>
      <StyledTypography variant="body2" weight={600}>
        Performance Grid
      </StyledTypography>
      <GridChartContainer>
        <StyledBox display="flex" flexDirection="column" justifyContent="center" width="100%" height="100%">
          {gridDefinition.map((row, index) => {
            const height = 100 / gridDefinition.length;
            return (
              <PerformanceGridRow
                key={index}
                rowData={row}
                height={`${gridDefinition.length > 1 ? height : 75}%`}
                hasMultipleRows={gridDefinition.length !== 1}
                horizontalIndex={index}
                rowsCount={gridDefinition?.length}
                row={index}
                onSelect={onSelectHandler}
                sectionId={sectionId}
                dataKey={dataKey}
                isAllEmployeesSelected={isAllEmployeesSelected}
                accessibleRole={accessibleRole}
                moduleName={moduleName}
              />
            );
          })}
        </StyledBox>
      </GridChartContainer>
    </>
  );
};

export default CohortCreation9BoxGrid;

export const PerformanceGridBox = ({
  color: selectedColor,
  label,
  percentage,
  count,
  scoreRange,
  hasMultipleRows = false,
  afterContent = null,
  beforeContent = null,
  isNoRating = false,
  row,
  column,
  onSelect,
  sectionId,
  dataKey,
  columnsCount,
  rowsCount,
  accessibleRole,
  isAllEmployeesSelected,
  moduleName,
}) => {
  const {
    state: { selectedOptions },
    updateBaseCohort,
  } = useCohortCreationContext();
  const dispatch = useDispatch();

  const id = camelCase(label);
  const selectedValues =
    !_.isEmpty(selectedOptions) && selectedOptions[sectionId]
      ? selectedOptions[sectionId][dataKey]
        ? selectedOptions[sectionId][dataKey]
        : null
      : null;

  const selectedIds = selectedValues ? Object.values(selectedValues).map((v) => v.id) : null;

  const isSelected = selectedIds ? selectedIds.includes(id) : false;
  const color = isSelected ? selectedColor : getColorFromKey('grey');
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  return (
    <StyledPerformanceGridBoxWrapper
      afterContent={afterContent}
      beforeContent={beforeContent}
      style={{ flex: '1 0 auto', width: `${100 / columnsCount}%` }}
    >
      <StyledTooltip title={label}>
        <StyledGridBox
          isNoRating={isNoRating}
          color={color}
          style={{
            cursor: 'pointer',
            width: '90%',
          }}
          onClick={() =>
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              () => onSelect({ row: rowsCount - row, column: column + 1, id, isSelected }),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            )
          }
        >
          <StyledGrid container justify="space-between">
            <StyledTypography variant="body2" color={color} weight={600} truncate>
              {label}
            </StyledTypography>
          </StyledGrid>
        </StyledGridBox>
      </StyledTooltip>
    </StyledPerformanceGridBoxWrapper>
  );
};

const PerformanceGridRow = ({
  rowData,
  height,
  hasMultipleRows,
  horizontalIndex,
  rowsCount,
  row,
  onSelect,
  sectionId,
  dataKey,
  accessibleRole,
  isAllEmployeesSelected,
  moduleName,
}) => (
  <StyledGrid
    container
    style={{
      height,
      padding: '0.5rem',
    }}
  >
    {rowData?.map((gridBox, verticalIndex) => {
      const { color, label } = gridBox;
      return (
        <PerformanceGridBox
          key={label}
          label={label}
          color={getColorFromKey(color)}
          hasMultipleRows={hasMultipleRows}
          row={row}
          column={verticalIndex}
          onSelect={onSelect}
          sectionId={sectionId}
          dataKey={dataKey}
          columnsCount={rowData?.length}
          rowsCount={rowsCount}
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={moduleName}
        />
      );
    })}
  </StyledGrid>
);

const GridBox = styled.div`
  // display: flex;
  // flex-direction: column;
  // min-height: 6rem;
  // height: 100%;
  // flex: 1;
  background-color: ${(props) => `${props.color}40`};
  padding: 0.5rem;
`;
const StyledGridBox = styled(GridBox)`
  border: 1px solid transparent;
  &:hover {
    border: 1px solid ${(props) => props.color ?? 'transparent'};
  }
`;

const GridChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 1rem;
`;

const StyledPerformanceGridBoxWrapper = styled(GridBox)`
  padding: 0;
  margin: 0;
  position: relative;
   {
    ${(props) =>
      props.beforeContent &&
      css`
        &::before {
          display: block;
          position: absolute;
          content: '${(props) => props.beforeContent ?? ''}';
          left: -2.5rem;
          top: -1rem;
          color: ${theme.palette.text.label};
          font-size: 0.75rem;
        }
      `}
  }
   {
    ${(props) =>
      props.afterContent &&
      css`
        &::after {
          display: block;
          position: absolute;
          content: '${(props) => props.afterContent ?? ''}';
          right: 0;
          bottom: -2.5rem;
          color: ${theme.palette.text.label};
          font-size: 0.75rem;
        }
      `}
  }
`;
